/**
 * Helpers for inspecting an action/trigger's `inputs` block to extract the
 * connector identity that should drive downstream calls (manifest fetch,
 * connection picker filtering, swagger lookups, etc.).
 *
 * Two shapes are supported, mirroring the workflow-definition spec:
 *   - **ApiConnection** family: `inputs.host.{ apiId, connection.referenceName }`
 *   - **ServiceProvider**: `inputs.serviceProviderConfiguration.{ operationId, connectionName }`
 *
 * Helpers return `null` (not `undefined`) so callers can chain via `??`
 * without optional-chaining dance.
 */

type ActionLike = { inputs?: unknown } | null | undefined;

interface HostShape {
  apiId?: unknown;
  connection?: { referenceName?: unknown } | undefined;
}

interface ServiceProviderShape {
  operationId?: unknown;
  connectionName?: unknown;
}

function getInputs(action: ActionLike): Record<string, unknown> | null {
  if (!action || typeof action !== 'object') return null;
  const inputs = (action as { inputs?: unknown }).inputs;
  return inputs && typeof inputs === 'object' ? (inputs as Record<string, unknown>) : null;
}

/**
 * Read `inputs.host.apiId` if present and non-empty (e.g.
 * `/subscriptions/.../managedApis/sharepointonline`).
 *
 * Use this only as a hint — for a *resolved* connector ID, prefer
 * `getConnectorIdForAction` which also chases the connection store.
 */
export function getActionHostApiId(action: ActionLike): string | null {
  const host = getInputs(action)?.['host'] as HostShape | undefined;
  return typeof host?.apiId === 'string' && host.apiId.length > 0 ? host.apiId : null;
}

/**
 * Read the connection reference name an action points at, regardless of the
 * action family.
 *
 * - ApiConnection: `inputs.host.connection.referenceName`
 * - ServiceProvider: `inputs.serviceProviderConfiguration.connectionName`
 * - Agent: `inputs.modelConfigurations.{modelN}.referenceName` (prefers model1, then first match)
 */
export function getActionReferenceName(action: ActionLike): string | null {
  const inputs = getInputs(action);
  if (!inputs) return null;

  const host = inputs['host'] as HostShape | undefined;
  if (typeof host?.connection?.referenceName === 'string' && host.connection.referenceName) {
    return host.connection.referenceName;
  }

  const sp = inputs['serviceProviderConfiguration'] as ServiceProviderShape | undefined;
  if (typeof sp?.connectionName === 'string' && sp.connectionName) {
    return sp.connectionName;
  }

  // Agent connections: inputs.modelConfigurations.{modelN}.referenceName
  if (inputs['modelConfigurations']) {
    const modelConfigs = inputs['modelConfigurations'] as Record<string, unknown>;
    // Prefer model1 (primary), then fall back to any other slot
    const model1 = modelConfigs.model1 as Record<string, unknown> | undefined;
    if (typeof model1?.referenceName === 'string' && model1.referenceName) {
      return model1.referenceName;
    }
    for (const value of Object.values(modelConfigs)) {
      if (value && typeof value === 'object') {
        const config = value as Record<string, unknown>;
        if (typeof config.referenceName === 'string' && config.referenceName) {
          return config.referenceName;
        }
      }
    }
  }

  return null;
}

/**
 * Resolve the canonical connector ID for an action, preferring sources that
 * carry the real `api.id`:
 *   1. `inputs.host.apiId` (when declared inline).
 *   2. `connectionReferences[referenceName].api.id` (the live store value).
 *
 * Returns `null` when neither is available — synthesizing a path from the
 * reference name is unsafe (opaque keys like `apiconnection-0576` produce
 * 404'ing `/managedApis/apiconnection-0576/...` URLs).
 */
export function getConnectorIdForAction(
  action: ActionLike,
  connectionReferences: Record<string, { api?: { id?: string } }>,
): string | null {
  const fromHost = getActionHostApiId(action);
  if (fromHost) return fromHost;

  const refName = getActionReferenceName(action);
  return refName ? connectionReferences[refName]?.api?.id ?? null : null;
}

/**
 * Extract a `/serviceProviders/{name}` connector ID from a ServiceProvider
 * action's `operationId`, e.g. `"/serviceProviders/AzureBlob/..." → "/serviceProviders/AzureBlob"`.
 */
export function getServiceProviderConnectorId(action: ActionLike): string | null {
  const sp = getInputs(action)?.['serviceProviderConfiguration'] as ServiceProviderShape | undefined;
  const opId = sp?.operationId;
  if (typeof opId !== 'string') return null;
  const parts = opId.split('/');
  const idx = parts.indexOf('serviceProviders');
  return idx !== -1 && parts[idx + 1] ? `/serviceProviders/${parts[idx + 1]}` : null;
}
